import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Reveal, SectionTag, EASE } from "../components/bits";
import SEO from "../components/SEO";

const STAGES = ["All", "Seedling", "Veg", "Bloom"];
const WATT_BUCKETS = [
  { id: "all", label: "Any power", test: () => true },
  { id: "u100", label: "Under 100W", test: (w) => w < 100 },
  { id: "100-300", label: "100–300W", test: (w) => w >= 100 && w <= 300 },
  { id: "300-600", label: "300–600W", test: (w) => w > 300 && w <= 600 },
  { id: "600p", label: "600W+", test: (w) => w > 600 },
];

export default function Shop() {
  const [products, setProducts] = useState(() => window.__INITIAL_DATA__?.products || []);
  const [loading, setLoading] = useState(() => !window.__INITIAL_DATA__?.products);
  const [series, setSeries] = useState("All");
  const [stage, setStage] = useState("All");
  const [watt, setWatt] = useState("all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const seriesList = useMemo(() => ["All", ...new Set(products.map((p) => p.series))], [products]);

  const filtered = useMemo(() => {
    const bucket = WATT_BUCKETS.find((b) => b.id === watt);
    let out = products.filter((p) => {
      if (series !== "All" && p.series !== series) return false;
      if (stage !== "All" && !p.stages.includes(stage)) return false;
      if (bucket && watt !== "all" && !p.variants.some((v) => bucket.test(v.led_watts))) return false;
      return true;
    });
    const minPrice = (p) => Math.min(...p.variants.map((v) => v.price));
    if (sort === "price-asc") out = [...out].sort((a, b) => minPrice(a) - minPrice(b));
    if (sort === "price-desc") out = [...out].sort((a, b) => minPrice(b) - minPrice(a));
    if (sort === "featured") out = [...out].sort((a, b) => Number(b.featured) - Number(a.featured));
    return out;
  }, [products, series, stage, watt, sort]);

  const chip = (active) =>
    `rounded-full px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.18em] transition-all ${
      active ? "bg-[#39FF14] text-[#050B07]" : "border border-[#39FF14]/20 text-[#86EFAC] hover:border-[#39FF14]/50"
    }`;

  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-28 sm:px-8" data-testid="shop-page">
      <SEO
        title="Shop LED Grow Lights — Quantum Sun, StarShip, Galaxy & More | SuperTechLED"
        description="Browse the full SuperTechLED range: Quantum Sun boards, StarShip bar lights, Galaxy, CommandShip and more. Samsung & Cree diodes, Meanwell drivers, free express shipping across Australia."
      />
      <Reveal>
        <SectionTag>The full range</SectionTag>
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Every light we make
        </h1>
        <p className="mt-3 max-w-xl text-sm text-[#9FB8A8] sm:text-base">
          From a 56W starter COB to the 1000W StarShip — every light in the range runs Samsung and Cree diodes,
          all Meanwell-driven, all shipped free across Australia.
        </p>
      </Reveal>

      <Reveal delay={0.1} className="mt-10 space-y-5">
        <div className="flex flex-wrap items-center gap-2" data-testid="filter-series">
          <span className="mr-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]/70">Series</span>
          {seriesList.map((s) => (
            <button key={s} data-testid={`filter-series-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setSeries(s)} className={chip(series === s)}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="filter-stage">
          <span className="mr-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]/70">Stage</span>
          {STAGES.map((s) => (
            <button key={s} data-testid={`filter-stage-${s.toLowerCase()}`} onClick={() => setStage(s)} className={chip(stage === s)}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="filter-wattage">
            <span className="mr-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]/70">Power</span>
            {WATT_BUCKETS.map((b) => (
              <button key={b.id} data-testid={`filter-watt-${b.id}`} onClick={() => setWatt(b.id)} className={chip(watt === b.id)}>
                {b.label}
              </button>
            ))}
          </div>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-[#39FF14]/25 bg-[#0A150F] px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#86EFAC] outline-none"
          >
            <option value="featured">Featured first</option>
            <option value="price-asc">Price · low to high</option>
            <option value="price-desc">Price · high to low</option>
          </select>
        </div>
      </Reveal>

      {loading ? (
        <div className="mt-16 text-center font-mono2 text-xs uppercase tracking-[0.3em] text-[#4ADE80]">Loading the range…</div>
      ) : (
        <>
          <div className="mt-8 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]/70" data-testid="shop-result-count">
            {`${filtered.length} ${filtered.length === 1 ? "light" : "products"}`}
          </div>
          <motion.div layout className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" transition={{ ease: EASE }}>
            {filtered.map((p, i) => (
              <ProductCard key={p.slug} p={p} i={i} />
            ))}
          </motion.div>
          {filtered.length === 0 && (
            <div className="mt-16 text-center text-sm text-[#9FB8A8]" data-testid="shop-empty">
              No lights match that combo — try clearing a filter.
            </div>
          )}
        </>
      )}
    </div>
  );
}
